import BaseAlert from '@/components/base/BaseAlert';
import BaseDateText from '@/components/base/BaseDateText';
import BaseSection from '@/components/base/BaseSection';
import BaseTag from '@/components/base/BaseTag';
import DetailTable from '@/components/composite/DetailTable';
import { useIntl } from '@/i18n';
import type { AdminAccountDetail } from '@/types';

interface AdminAccountBasicProps {
  detail: AdminAccountDetail;
}

/** 基本资料与权限摘要沿用正式字段，日期交由基础组件处理浏览器时区。 */
export default function AdminAccountBasic({ detail }: AdminAccountBasicProps) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, unknown>) =>
    intl.formatMessage({ id }, values);
  const owner = detail.accountKind === 'tenant';
  const status = (
    <BaseTag color={detail.status === 'normal' ? 'green' : 'red'}>
      {t(`adminAcct.status.${detail.status}`)}
    </BaseTag>
  );
  const permission = detail.permission;
  const archive = detail.boundArchive;
  const scope = (value: string) =>
    ['all', 'dept', 'self'].includes(value) ? t(`scope.${value}`) : value;
  const posts = detail.posts.length ? (
    detail.posts.map((post, index) => (
      <div key={`${post.name}-${index}`}>
        <BaseTag
          color={
            post.source === 'builtin'
              ? 'red'
              : post.source === 'title'
              ? 'blue'
              : 'orange'
          }
        >
          {post.name}
        </BaseTag>
        <span className="text-xs text-secondary">
          {t(`adminAcct.detail.source.${post.source}`)}
          {post.validTo && ` · ${post.validTo}`}
        </span>
      </div>
    ))
  ) : (
    <>
      <BaseTag color="orange">{t('adminAcct.detail.noPosts')}</BaseTag>
      <div className="text-xs text-secondary">{t('adminAcct.detail.bare')}</div>
    </>
  );

  return (
    <div className="flex flex-col gap-4">
      {' '}
      {owner && (
        <BaseAlert
          type="info"
          showIcon
          title={t('adminAcct.detail.ownerNotice')}
        />
      )}
      <DetailTable
        items={[
          {
            key: 'name',
            label: t('adminAcct.field.accountName'),
            children: (
              <>
                <b>{detail.accountName ?? '—'}</b>
                <div className="text-xs text-secondary">
                  {detail.nameSource ??
                    t('adminAcct.detail.nameSourceFallback')}
                </div>
              </>
            ),
          },
          {
            key: 'kind',
            label: t('adminAcct.detail.accountType'),
            children: (
              <BaseTag color={owner ? 'red' : 'blue'}>
                {t(
                  owner
                    ? 'adminAcct.detail.ownerKind'
                    : 'adminAcct.detail.adminKind',
                )}
              </BaseTag>
            ),
          },
          {
            key: 'email',
            label: t('adminAcct.field.loginEmail'),
            children: (
              <span className="break-all font-mono text-xs">
                {detail.loginEmail}
              </span>
            ),
          },
          {
            key: 'archive',
            label: t('adminAcct.detail.boundStaff'),
            children:
              archive && archive.type !== 'none' ? (
                <span className={archive.stale ? 'text-error' : undefined}>
                  {archive.name} {archive.no}
                </span>
              ) : (
                t(
                  owner
                    ? 'adminAcct.kind.tenant'
                    : 'adminAcct.detail.unassociated',
                )
              ),
          },
          {
            key: 'titles',
            label: t('adminAcct.detail.titles'),
            children: detail.titles.length
              ? detail.titles.map((title, index) => (
                  <div key={index}>
                    <BaseTag color="blue">{title.title}</BaseTag>
                  </div>
                ))
              : t(
                  archive && archive.type !== 'none'
                    ? 'adminAcct.detail.noActiveAppointment'
                    : 'adminAcct.detail.unassociated',
                ),
          },
          {
            key: 'status',
            label: t('adminAcct.detail.accountStatus'),
            children: (
              <>
                {status}
                {owner && (
                  <div className="text-xs text-secondary">
                    {t('adminAcct.detail.cannotDisable')}
                  </div>
                )}
              </>
            ),
          },
          {
            key: 'created',
            label: t('adminAcct.detail.createdAt'),
            value: detail.createdAt,
            valueType: 'dateTime',
            format: 'YYYY-MM-DD HH:mm',
            children: detail.createdAt ? undefined : '—',
          },
          {
            key: 'last',
            label: t('adminAcct.detail.lastLogin'),
            children: (
              <>
                <span className="font-mono text-xs">
                  {detail.lastLoginAt ? (
                    <BaseDateText
                      value={detail.lastLoginAt}
                      type="dateTime"
                      format="YYYY-MM-DD HH:mm"
                    />
                  ) : (
                    t('adminAcct.detail.neverLogin')
                  )}
                </span>
                {detail.lastLoginIp && (
                  <div className="font-mono text-xs text-secondary">
                    {detail.lastLoginIp}
                  </div>
                )}
              </>
            ),
          },
        ]}
      />
      <BaseSection
        title={t('adminAcct.detail.permissionSummary')}
        extra={
          <span className="text-xs text-secondary">
            {t('adminAcct.detail.summaryHint')}
          </span>
        }
      >
        <DetailTable
          items={[
            {
              key: 'pages',
              label: t('adminAcct.detail.pages'),
              children: permission
                ? permission.allPages
                  ? t('adminAcct.detail.allPages')
                  : t('adminAcct.detail.pageCount', {
                      count: permission.pageCount,
                    })
                : '—',
            },
            {
              key: 'buttons',
              label: t('adminAcct.detail.buttons'),
              children: permission
                ? permission.allPages
                  ? t('adminAcct.detail.allButtons')
                  : t('adminAcct.detail.buttonCount', {
                      count: permission.buttonCount,
                    })
                : '—',
            },
            {
              key: 'scope',
              label: t('adminAcct.detail.scope'),
              children: permission ? scope(permission.dataScope) : '—',
            },
            {
              key: 'posts',
              label: t('adminAcct.field.posts'),
              children: posts,
            },
          ]}
        />
      </BaseSection>
    </div>
  );
}
